import React, { useEffect, useId, useRef, useState } from 'react'
import classNames from 'classnames'
import { useKeyboardNavigation } from '../../../../common/useKeyboardNavigation'
import { Button } from '../../../../components/Button/Button'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientClients } from '../../../../store/crossClient/crossClientResults'
import { CrossClientResultSource } from '../Sources/CrossClientResultSource'
import { CrossClientViewProfileButton } from './CrossClientViewProfileButton'
import { ActionsDropdownItem } from '../../Actions/CrossClientActionsDropdown'
import {
  CCQ_DECIDING_STATE,
  CcqCards,
  CcqCriterionStates,
  ccqCriterionTitle,
  ccqOffersDeepAnalysis,
  decidingCriteria,
  type CcqCard,
  type CcqCriterionState,
  type CcqCriterionView,
} from './ccqResultView'
import { CcqActionButtons } from './CcqActionButtons'
import { CcqActionTag } from './CcqActionTag'
import { CcqAiGuidance } from './CcqAiGuidance'
import { CcqStatusChip } from './CcqStatusChip'
import {
  CCQ_CRITERIA_HIDE_ALL,
  CCQ_CRITERIA_SHOW_ALL,
  CCQ_CRITERION_MET,
  CCQ_CRITERION_NO_DATA,
  CCQ_CRITERION_NOT_MET,
  CCQ_NOT_CHECKED,
  CCQ_OPEN_PROFILE,
  CCQ_PANEL_CLOSE,
  CCQ_PANEL_NEXT,
  CCQ_PANEL_PREVIOUS,
  CCQ_SEND_EMAIL,
  ccqNotFoundIn,
  ccqOutOf,
  ccqOwnerLabel,
  ccqSourceNames,
} from './ccqRunCopy'
import { useCcqClientView } from './useCcqClientView'
import { useCcqRun } from './useCcqRun'
import { useCcqCursor } from './useCcqListCursor'
import { useCcqWorkspace } from './useCcqWorkspace'
import { CcqFillAnchors, useFillToViewportBottom } from './useFillToViewportBottom'
import styles from './CcqRail.module.scss'

// A filled green check for met (the only filled marker), and in grey an X for not met and a dash for not found.
// Not checked has no marker: its slot stays empty so the title still lines up.
const VERDICT_MARKER: Partial<Record<CcqCriterionState, { icon: string; tone: string }>> = {
  [CcqCriterionStates.MET]: { icon: 'bi-check-circle-fill', tone: styles.markerConfirmed },
  [CcqCriterionStates.NOT_MET]: { icon: 'bi-x-lg', tone: styles.markerGray },
  [CcqCriterionStates.NOT_FOUND]: { icon: 'bi-dash-lg', tone: styles.markerGray },
}

const ReviewMarker: React.FC<{ state: CcqCriterionState }> = ({ state }) => {
  const marker = VERDICT_MARKER[state]
  return marker ? (
    <i className={classNames('bi flex-shrink-0', marker.icon, styles.criterionGlyph, marker.tone)} aria-hidden='true' />
  ) : (
    <span className={classNames('flex-shrink-0', styles.criterionGlyph)} aria-hidden='true' />
  )
}

interface CriterionRowProps {
  criterion: CcqCriterionView
  sourceNames: string[]
}

const hasVerdict = (criterion: CcqCriterionView): boolean => criterion.state === CcqCriterionStates.MET || criterion.state === CcqCriterionStates.NOT_MET

const toneOf = (criterion: CcqCriterionView): string => (criterion.state === CcqCriterionStates.NOT_CHECKED ? 'zocks-text-gray-600' : 'zocks-text-gray-900')

interface ReviewTitleProps {
  criterion: CcqCriterionView
  isSingleLine?: boolean
}

const ReviewTitle: React.FC<ReviewTitleProps> = ({ criterion, isSingleLine = false }) => (
  <>
    <ReviewMarker state={criterion.state} />
    {/* The marker is decorative and the explanation is free text, so the verdict is spelled out for screen readers. */}
    {hasVerdict(criterion) ? (
      <span className='visually-hidden'>{criterion.state === CcqCriterionStates.MET ? CCQ_CRITERION_MET : CCQ_CRITERION_NOT_MET}</span>
    ) : null}
    <span className={classNames({ 'text-truncate': isSingleLine })}>{ccqCriterionTitle(criterion.clause)}</span>
    {criterion.state === CcqCriterionStates.NOT_FOUND ? (
      <span className='paragraph-small fw-normal zocks-text-gray-600 text-nowrap flex-shrink-0'>{CCQ_CRITERION_NO_DATA}</span>
    ) : null}
  </>
)

const reasonFor = (criterion: CcqCriterionView, sourceNames: string[]): string =>
  criterion.showsExplanation ? criterion.explanation : criterion.state === CcqCriterionStates.NOT_FOUND ? ccqNotFoundIn(sourceNames) : CCQ_NOT_CHECKED

// "Not found in Zocks or Redtail." → "Not found in Zocks or Redtail", "(see notes.)" → "(see notes)"
const withoutFinalStop = (reason: string): string => reason.trim().replace(/[.!?…]+(?=[)\]"'”’]*$)/, '')

// A heading over two or more not found rows that give the exact same reason, or (heading null) a row of its own.
interface CriteriaEntry {
  heading: string | null
  criteria: CcqCriterionView[]
}

// Each group takes the place of its first row, so the entries keep the query's order.
const groupSharedReasons = (criteria: CcqCriterionView[], sourceNames: string[]): CriteriaEntry[] => {
  const headings = criteria.map((criterion) => (criterion.state === CcqCriterionStates.NOT_FOUND ? withoutFinalStop(reasonFor(criterion, sourceNames)) : ''))
  const isShared = (heading: string) => heading !== '' && headings.filter((each) => each === heading).length >= 2
  return criteria.flatMap((criterion, index): CriteriaEntry[] => {
    const heading = headings[index]
    if (!isShared(heading)) {
      return [{ heading: null, criteria: [criterion] }]
    }
    return headings.indexOf(heading) === index ? [{ heading, criteria: criteria.filter((_, other) => headings[other] === heading) }] : []
  })
}

interface SharedReasonEntryProps {
  heading: string
  criteria: CcqCriterionView[]
}

const SharedReasonEntry: React.FC<SharedReasonEntryProps> = ({ heading, criteria }) => (
  <li className='d-flex flex-column gap-2'>
    <span className='paragraph-medium bold zocks-text-gray-900'>{`${heading}:`}</span>
    <ul className='list-unstyled d-flex flex-column gap-2 mb-0'>
      {criteria.map((criterion) => (
        <li key={criterion.clause.variable} className='d-flex align-items-center gap-2 paragraph-medium zocks-text-gray-900'>
          <ReviewMarker state={criterion.state} />
          {ccqCriterionTitle(criterion.clause)}
        </li>
      ))}
    </ul>
  </li>
)

const ReviewDetails: React.FC<CriterionRowProps> = ({ criterion, sourceNames }) => {
  const line = reasonFor(criterion, sourceNames)
  return (
    <>
      <span className={classNames('paragraph-medium ps-4', toneOf(criterion))}>{line}</span>
      {hasVerdict(criterion) && criterion.source ? <CrossClientResultSource resultSource={criterion.source} /> : null}
    </>
  )
}

const ReviewEntry: React.FC<CriterionRowProps> = ({ criterion, sourceNames }) => (
  <li className={classNames('d-flex flex-column', hasVerdict(criterion) ? 'gap-2' : 'gap-1')}>
    <span className={classNames('d-flex align-items-center gap-2 paragraph-medium bold', toneOf(criterion))}>
      <ReviewTitle criterion={criterion} />
    </span>
    <ReviewDetails criterion={criterion} sourceNames={sourceNames} />
  </li>
)

interface CompactEntryProps extends CriterionRowProps {
  isOpen: boolean
  onToggle: () => void
}

const CompactEntry: React.FC<CompactEntryProps> = ({ criterion, sourceNames, isOpen, onToggle }) => {
  const detailsId = useId()
  const gap = hasVerdict(criterion) ? 'gap-2' : 'gap-1'
  return (
    <li className={classNames('d-flex flex-column', gap)}>
      <Button
        variant='borderless'
        className='d-flex align-items-center justify-content-start gap-2 w-100 p-0 border-0 rounded-0 text-start'
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={detailsId}
      >
        <span className={classNames('d-flex align-items-center gap-2 flex-grow-1 overflow-hidden paragraph-medium bold', toneOf(criterion))}>
          <ReviewTitle criterion={criterion} isSingleLine />
        </span>
        <i className={classNames('bi flex-shrink-0 zocks-text-gray-600', isOpen ? 'bi-chevron-up' : 'bi-chevron-down')} aria-hidden='true' />
      </Button>
      {isOpen ? (
        <div id={detailsId} className={classNames('d-flex flex-column', gap)}>
          <ReviewDetails criterion={criterion} sourceNames={sourceNames} />
        </div>
      ) : null}
    </li>
  )
}

const NavButton: React.FC<{ label: string; icon: string; isDisabled: boolean; onClick: () => void }> = ({ label, icon, isDisabled, onClick }) => (
  <Tooltip title={label} placement='top'>
    <Button
      variant='borderless'
      className={classNames(styles.navButton, 'd-flex align-items-center justify-content-center p-0 border-0')}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={label}
    >
      <i className={classNames('bi', icon)} aria-hidden='true' />
    </Button>
  </Tooltip>
)

const CcqMatchDetailsContent: React.FC<{ result: CrossClientQueryResult }> = ({ result }) => {
  const { execution } = useCcqRun()
  const { panel, isPanelClosing, takePanelFocusRequest } = useCcqWorkspace()
  const { close: closePanel, lastMove, position: panelPosition, next, previous } = useCcqCursor().details
  const panelStep = lastMove.direction
  const headingRef = useRef<HTMLHeadingElement>(null)
  const clients = useAppSelector(selectCrossClientClients)
  const view = useCcqClientView(result)
  // Measured from the grid: the sticky rail it sits in moves with the scroll.
  const panelRef = useFillToViewportBottom<HTMLElement>(true, CcqFillAnchors.GRID)

  useEffect(() => {
    if (takePanelFocusRequest()) {
      headingRef.current?.focus()
    }
  }, [view.resultId, takePanelFocusRequest])

  const openedFrom: CcqCard = panel?.card ?? view.card ?? CcqCards.NOT_IN_RECORDS
  const status = view.card ?? openedFrom

  // Highlights the chip when the shown client moves, not when stepping to another client. Adjusted during
  // render so the highlight starts on the frame the status changes.
  const [shown, setShown] = useState<{ resultId: string | null; status: CcqCard | null }>({ resultId: null, status: null })
  const [changedChip, setChangedChip] = useState<string | null>(null)
  if (shown.resultId !== view.resultId || shown.status !== status) {
    setShown({ resultId: view.resultId, status })
    setChangedChip(shown.resultId === view.resultId ? `${view.resultId}-${status}` : null)
  }

  // The compact rows open for the client shown, and whether they all are. That last part carries on as the
  // advisor steps through clients: each one then opens with every compact row open. Closing the panel resets
  // both: it unmounts this content, or, reopened before its exit ends, leaves the closing state.
  const [openedRows, setOpenedRows] = useState<{ resultId: string | null; variables: string[] }>({ resultId: null, variables: [] })
  const [opensAll, setOpensAll] = useState(false)
  const [wasClosing, setWasClosing] = useState(isPanelClosing)
  if (wasClosing !== isPanelClosing) {
    setWasClosing(isPanelClosing)
    if (!isPanelClosing) {
      setOpenedRows({ resultId: null, variables: [] })
      setOpensAll(false)
    }
  }

  useKeyboardNavigation({ onNext: next.go, onPrevious: previous.go, enableNext: next.isEnabled, enablePrevious: previous.isEnabled, enabled: !isPanelClosing })
  const showsActions = status === CcqCards.MATCHED
  const showsGuidance = ccqOffersDeepAnalysis(openedFrom) || status === CcqCards.MISSING_DATA
  // Only where the Not in records guidance stands in for them; a client with no card of its own keeps its criteria.
  const showsCriteria = view.card !== CcqCards.NOT_IN_RECORDS || openedFrom !== CcqCards.NOT_IN_RECORDS
  const client = clients[view.clientId]
  const sourceNames = ccqSourceNames(execution.sources)
  const hasDecidingState = CCQ_DECIDING_STATE[status] !== undefined
  const deciding = decidingCriteria(view.criteria, status)
  const openCriteria = hasDecidingState ? deciding : view.criteria
  const compactCriteria = hasDecidingState ? view.criteria.filter((criterion) => !deciding.includes(criterion)) : []
  const compactVariables = compactCriteria.map((criterion) => criterion.clause.variable)
  // A client the advisor hasn't opened or closed anything on yet starts as the last one left off.
  const openVariables = openedRows.resultId === view.resultId ? openedRows.variables : opensAll ? compactVariables : []
  const isAllOpen = compactVariables.every((variable) => openVariables.includes(variable))
  // Opening the last closed row one by one counts as Show all, closing any as Hide all. A client with a single
  // compact row has no Show all, so it leaves the carry-over alone.
  const setOpenVariables = (next: string[]) => {
    setOpenedRows({ resultId: view.resultId, variables: next })
    if (compactVariables.length >= 2) {
      setOpensAll(compactVariables.every((variable) => next.includes(variable)))
    }
  }
  const toggleRow = (variable: string) => {
    setOpenVariables(openVariables.includes(variable) ? openVariables.filter((each) => each !== variable) : [...openVariables, variable])
  }
  // The keys below remount the content per client to replay this; the toolbar isn't keyed, so it stays still.
  const swapClass = panelStep === 1 ? styles.swapNext : panelStep === -1 ? styles.swapPrevious : styles.swapFade
  const chipKey = `${view.resultId}-${status}`

  return (
    <section
      ref={panelRef}
      aria-labelledby='ccq-panel-heading'
      className={classNames(
        styles.panel,
        isPanelClosing ? styles.panelExit : styles.panelEnter,
        'd-flex flex-column border rounded-4 zocks-border-color-gray-300 zocks-bg-color-white',
      )}
    >
      <header className='d-flex flex-column gap-1 px-4 pb-4 flex-shrink-0'>
        <div className={classNames(styles.strip, 'd-flex align-items-center justify-content-between gap-3')}>
          <div className='d-flex align-items-center gap-1'>
            <NavButton label={CCQ_PANEL_PREVIOUS} icon='bi-chevron-left' isDisabled={!previous.isEnabled} onClick={previous.go} />
            {panelPosition !== null ? (
              <span className='px-1 paragraph-small zocks-text-gray-900 text-nowrap'>{ccqOutOf(panelPosition.position, panelPosition.total)}</span>
            ) : null}
            <NavButton label={CCQ_PANEL_NEXT} icon='bi-chevron-right' isDisabled={!next.isEnabled} onClick={next.go} />
          </div>
          <Tooltip title={CCQ_PANEL_CLOSE} placement='top'>
            <Button
              variant='borderless'
              className={classNames(styles.navButton, 'd-flex align-items-center justify-content-center p-0 border-0')}
              onClick={closePanel}
              aria-label={CCQ_PANEL_CLOSE}
            >
              <i className='bi bi-x-lg zocks-text-gray-900' aria-hidden='true' />
            </Button>
          </Tooltip>
        </div>
        <div key={view.resultId} className={classNames(swapClass, 'd-flex flex-wrap align-items-center gap-2')}>
          <h2 ref={headingRef} id='ccq-panel-heading' className='h5 mb-0' tabIndex={-1}>
            {view.name}
          </h2>
          <CcqStatusChip result={result} card={status} isHighlighted={changedChip === chipKey} highlightKey={chipKey} />
          {showsActions ? null : <CcqActionTag clientId={view.clientId} clientName={view.name} />}
        </div>
        {/* Each dot stays with the item before it, so a wrapped line always starts with a whole item. */}
        <div
          key={`${view.resultId}-meta`}
          className={classNames(swapClass, 'd-flex flex-wrap align-items-center column-gap-2 paragraph-small zocks-text-gray-700')}
        >
          {view.email ? (
            <span className='d-inline-flex align-items-center gap-2 text-nowrap'>
              {view.email}
              <span aria-hidden='true'>·</span>
            </span>
          ) : null}
          {view.owner !== '-' ? (
            <span className='d-inline-flex align-items-center gap-2 text-nowrap'>
              {ccqOwnerLabel(view.owner)}
              <span aria-hidden='true'>·</span>
            </span>
          ) : null}
          {client ? (
            <CrossClientViewProfileButton client={client} label={CCQ_OPEN_PROFILE} className={classNames(styles.metaLink, 'button-small text-nowrap')} />
          ) : null}
        </div>
        {showsActions ? (
          // Secondary: the MATCHED footer has the page's only primary button. Unlike bulk actions, these don't
          // wait for the check to finish.
          <div className='d-flex align-items-center gap-2 pt-3'>
            <CcqActionButtons
              isPrimary={false}
              isDisabled={false}
              resolveTargets={() => Promise.resolve([result])}
              count={1}
              scope='single'
              sendBookingInvitationsTarget={{ kind: 'clients', clientIds: [view.clientId] }}
              extraItems={<ActionsDropdownItem icon='bi-envelope' label={CCQ_SEND_EMAIL} to={`/email/new?contactId=${view.clientId}`} />}
              singleClient={{
                name: view.name,
                clientId: view.clientId,
                integrationServiceIds: client ? (client.integrations ?? []).map((integration) => integration.serviceId) : undefined,
              }}
            />
            <CcqActionTag clientId={view.clientId} clientName={view.name} />
          </div>
        ) : null}
      </header>

      {showsGuidance ? <CcqAiGuidance key={`${view.resultId}-guidance`} view={view} card={openedFrom} /> : null}

      {showsCriteria ? (
        <div key={view.resultId} className={classNames(styles.panelBody, swapClass, 'd-flex flex-column gap-4 p-4 border-top zocks-border-color-gray-200')}>
          {openCriteria.length > 0 ? (
            <ul className='list-unstyled d-flex flex-column gap-4 mb-0'>
              {groupSharedReasons(openCriteria, sourceNames).map(({ heading, criteria }) =>
                heading === null ? (
                  <ReviewEntry key={criteria[0].clause.variable} criterion={criteria[0]} sourceNames={sourceNames} />
                ) : (
                  <SharedReasonEntry key={`shared-${criteria[0].clause.variable}`} heading={heading} criteria={criteria} />
                ),
              )}
            </ul>
          ) : null}
          {compactCriteria.length > 0 ? (
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex align-items-center gap-3'>
                <span className={classNames('flex-grow-1', { 'border-top zocks-border-color-gray-200': openCriteria.length > 0 })} aria-hidden='true' />
                {compactCriteria.length >= 2 ? (
                  <Button
                    variant='primaryLinkButton'
                    className={classNames(styles.metaLink, 'button-small flex-shrink-0 p-0 border-0 text-nowrap')}
                    onClick={() => setOpenVariables(isAllOpen ? [] : compactVariables)}
                    aria-expanded={isAllOpen}
                    aria-controls='ccq-panel-compact-criteria'
                  >
                    {isAllOpen ? CCQ_CRITERIA_HIDE_ALL : CCQ_CRITERIA_SHOW_ALL}
                  </Button>
                ) : null}
              </div>
              <ul id='ccq-panel-compact-criteria' className='list-unstyled d-flex flex-column gap-3 mb-0'>
                {compactCriteria.map((criterion) => (
                  <CompactEntry
                    key={criterion.clause.variable}
                    criterion={criterion}
                    sourceNames={sourceNames}
                    isOpen={openVariables.includes(criterion.clause.variable)}
                    onToggle={() => toggleRow(criterion.clause.variable)}
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

export const CcqMatchDetailsPanel: React.FC = () => {
  const { panelResult } = useCcqWorkspace()
  return panelResult === null ? null : <CcqMatchDetailsContent result={panelResult} />
}
